import React, { Fragment, useEffect, useState } from "react";
import { FormSelect, FormSwitchList } from "components/form-fields";
import { UserAccess, UserHasAccess } from "components/user-access"
import { HelperFunctions } from "helpers";
import { ShowHide } from "components/layout";
import { AlertUnsavedData } from "components/notifications";
import { useDirtyData } from "hooks/useDirtyData";
import { usePsvScreensEnabled } from "hooks/usePsvScreensEnabled";
import { ButtonSave } from "components/buttons";
import PropTypes from 'prop-types';

const defaultObject = {};
const psvEuEligibleOptionId = 3;

// Matched on id because the rules engine pins Domestic to 1 (RuleType.Domestic); the option names vary.
const domesticAnalysisRulesId = 1;
const psvDomesticDisplayText = "PSV Domestic";

export function Preferences(
    {
        assetProfile = defaultObject,
        saveProfile = () => { },
        loading = true,
        saving = false,
        assetTypeDisplay = {},
        assetOptions = {}
    }
) {
    const requiredAction = ["Fleet:View", "FleetPCN:View", "FleetVIS:View"];
    const requiredEditActions = ["Fleet:Edit", "FleetPCN:Edit", "FleetVIS:Edit"];

    const canAccessPage = UserHasAccess(requiredAction);
    const canEditProfile = UserHasAccess(requiredEditActions);
    const [preferences, setPreferences] = useState({});
    const isDirty = useDirtyData(assetProfile, preferences);
    const psvScreensEnabled = usePsvScreensEnabled(assetProfile);

    // A label only: the stored value stays GB Domestic, so the options and the save are untouched.
    let tachographRulesDisplayText = null;
    if (psvScreensEnabled && preferences?.analysisRules?.id === domesticAnalysisRulesId) {
        tachographRulesDisplayText = psvDomesticDisplayText;
    }

    useEffect(() => {
        if (!canAccessPage || !assetProfile
            || !assetOptions || assetOptions.length < 1
            || !assetTypeDisplay || assetTypeDisplay.length < 1
        ) {
            return;
        }

        setPreferences(HelperFunctions.deepClone(assetProfile));
    }, [assetProfile, assetTypeDisplay, assetOptions]);

    function onAnalysisRulesChange(value) {
        setPreferences((prev) => {
            return {
                ...prev,
                analysisRules: value
            };
        });
    }

    function onVehicleOptionsChange(event) {
        var toggleId = parseInt(event.currentTarget.getAttribute('identifier'));
        if (toggleId === 1) {
            onDigitalVehicleChange(event.target.checked);
        }
        else if (toggleId === 2) {
            onVehicleMovedWithoutCardAlertChange(event.target.checked);
        }
        else if (toggleId === psvEuEligibleOptionId) {
            onPsvEuEligibleChange(event.target.checked);
        }
    }

    function onDigitalVehicleChange(value) {
        setPreferences((prev) => {
            var newInstance = {
                ...prev
            };
            if (!newInstance.isDigital) {
                newInstance.isDigital = false;
            }
            newInstance.isDigital = value;
            return newInstance;
        });
    }

    function onVehicleMovedWithoutCardAlertChange(value) {
        setPreferences((prev) => {
            var newInstance = {
                ...prev
            };
            if (!newInstance.assetLocation.alertAssignJourneyInclude) {
                newInstance.assetLocation.alertAssignJourneyInclude = false;
            }
            newInstance.assetLocation.alertAssignJourneyInclude = value;
            return newInstance;
        });
    }

    function onPsvEuEligibleChange(value) {
        setPreferences((prev) => {
            const newInstance = {
                ...prev
            };
            newInstance.assetLocation.psvEuEligible = value;
            return newInstance;
        });
    }

    function getVehicleOptions() {
        const vehicleOptions = [
            {
                id: 1,
                name: "Digital Vehicle",
                checked: preferences?.isDigital,
            },
            {
                id: 2,
                name: "Vehicle Moved Without Card Alert",
                checked: preferences?.assetLocation?.alertAssignJourneyInclude,
            }
        ];

        if (psvScreensEnabled) {
            vehicleOptions.push({
                id: psvEuEligibleOptionId,
                name: "Occasional international coach work (12-day rule)",
                checked: preferences?.assetLocation?.psvEuEligible === true,
            });
        }

        return vehicleOptions;
    }

    function savePreferences() {
        if (!canEditProfile) {
            return;
        }
        saveProfile(preferences);
    }

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <div className="row">
                    <h3>Preferences</h3>
                    <FormSelect
                        id="tachograph-rules"
                        headingText="Tachograph Rules"
                        items={assetOptions.analysisRules}
                        onItemSelect={onAnalysisRulesChange}
                        selectedValue={preferences?.analysisRules?.id}
                        displayText={tachographRulesDisplayText}
                        disabled={!canEditProfile || saving}
                        dynamicWidth={true}
                        loading={loading}
                    />

                    <FormSwitchList
                        items={getVehicleOptions()}
                        onChange={onVehicleOptionsChange}
                        headingText="Vehicle Options"
                        loading={loading}
                        switchListName="vehicleOptionsList"
                        disabled={!canEditProfile || saving}
                        dangerHelperText=""
                        headingLevel="h3"
                    />

                    <ShowHide
                        evaluator={canEditProfile}
                        show={(
                            <Fragment>
                                <AlertUnsavedData
                                    isDirty={isDirty}
                                    scrollToBottomOnCancel={true}
                                >
                                </AlertUnsavedData>

                                <ButtonSave text="Save"
                                    disabled={!canEditProfile || saving}
                                    simpleDisabled={!isDirty}
                                    loading={loading}
                                    onClick={savePreferences}
                                />
                            </Fragment>
                        )}
                    />
                </div>
            )}
        />
    );
}

Preferences.propTypes = {
    assetProfile: PropTypes.object,
    saveProfile: PropTypes.func,
    assetTypeDisplay: PropTypes.array.isRequired,
    assetOptions: PropTypes.object.isRequired,
    loading: PropTypes.bool.isRequired,
    saving: PropTypes.bool.isRequired
};